import React, { useState, useEffect } from 'react';
import { safeLocalStorage } from '@/lib/storage-utils';
import { Menu, ChevronDown, ChevronRight } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { navigationItems, DEFAULT_HIDDEN_MENU_IDS } from '@/lib/navigation';
import { supabase } from '@/lib/supabase';
import { useWorkspace } from '@/context/WorkspaceContext';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface Workspace {
  id: string;
  name: string;
  appearance?: {
    menu?: {
      hiddenIds?: string[];
      roleHiddenIds?: Record<string, string[]>;
    };
    [key: string]: any;
  };
  [key: string]: any;
}

interface RolesPermissionsTabProps {
  workspace: Workspace;
  isGlobalDarkMode: boolean;
}

export const RolesPermissionsTab: React.FC<RolesPermissionsTabProps> = ({ workspace }) => {
  const { refreshWorkspaces } = useWorkspace();
  const [activeRole, setActiveRole] = useState('all');

  const [hiddenMenuIds, setHiddenMenuIds] = useState<string[]>(
    workspace.appearance?.menu?.hiddenIds || DEFAULT_HIDDEN_MENU_IDS
  );
  
  const [roleHiddenIds, setRoleHiddenIds] = useState<Record<string, string[]>>(
    workspace.appearance?.menu?.roleHiddenIds || {}
  );

  const [collapsedMenus, setCollapsedMenus] = useState<Record<string, boolean>>(() => {
    const initialState: Record<string, boolean> = {};
    navigationItems.forEach(item => {
      if (item.hasSubItems) {
        initialState[item.id] = true;
      }
    });
    return initialState;
  });

  useEffect(() => {
    if (workspace.appearance?.menu) {
      setHiddenMenuIds(workspace.appearance.menu.hiddenIds || DEFAULT_HIDDEN_MENU_IDS);
      setRoleHiddenIds(workspace.appearance.menu.roleHiddenIds || {});
    }
  }, [workspace.id, workspace.appearance]);

  const toggleMenuCollapse = (id: string) => {
    setCollapsedMenus(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const currentHiddenIds = activeRole === 'all' ? hiddenMenuIds : (roleHiddenIds[activeRole] || hiddenMenuIds);

  const saveWorkspaceAppearance = (menuUpdates: any) => {
    const savedWorkspaces = safeLocalStorage.getItem('userWorkspaces');
    if (savedWorkspaces) {
      const workspaces = JSON.parse(savedWorkspaces);
      const updatedWorkspaces = workspaces.map((w: Workspace) => {
        if (w.id === workspace.id) {
          return {
            ...w,
            appearance: {
              ...w.appearance,
              menu: {
                ...w.appearance?.menu,
                ...menuUpdates
              },
            },
          };
        }
        return w;
      });
      safeLocalStorage.setItem('userWorkspaces', JSON.stringify(updatedWorkspaces));
    }
  };

  const saveToDatabase = async (menuUpdates: any) => {
    try {
      const updatedAppearance = {
        ...workspace.appearance,
        menu: {
          ...workspace.appearance?.menu,
          ...menuUpdates
        }
      };

      const { error } = await supabase
        .from('workspaces')
        .update({
          appearance: updatedAppearance
        })
        .eq('id', workspace.id);

      if (error) throw error;

      await refreshWorkspaces();
      return true;
    } catch (error) {
      console.error('Error saving menu to database:', error);
      return false;
    }
  };

  const handleMenuToggle = async (id: string, checked: boolean) => {
    let newHiddenIds = [...currentHiddenIds];
    const item = navigationItems.find(i => i.id === id);

    if (checked) {
      newHiddenIds = newHiddenIds.filter(hiddenId => hiddenId !== id);
      if (item?.subItems) {
        const subItemIds = item.subItems.map(si => si.id);
        newHiddenIds = newHiddenIds.filter(hiddenId => !subItemIds.includes(hiddenId));
      }
    } else {
      if (!newHiddenIds.includes(id)) newHiddenIds.push(id);
      if (item?.subItems) {
        item.subItems.forEach(subItem => {
          if (!newHiddenIds.includes(subItem.id)) newHiddenIds.push(subItem.id);
        });
      }
    }

    let menuUpdates: any = {};
    if (activeRole === 'all') {
      setHiddenMenuIds(newHiddenIds);
      menuUpdates = { hiddenIds: newHiddenIds, roleHiddenIds };
    } else {
      const newRoleHiddenIds = { ...roleHiddenIds, [activeRole]: newHiddenIds };
      setRoleHiddenIds(newRoleHiddenIds);
      menuUpdates = { hiddenIds: hiddenMenuIds, roleHiddenIds: newRoleHiddenIds };
    }

    saveWorkspaceAppearance(menuUpdates);
    const success = await saveToDatabase(menuUpdates);

    if (success) {
      toast.success(`Visibility updated for ${activeRole === 'all' ? 'All Roles' : activeRole}`);
    } else {
      toast.error('Failed to save menu settings');
      // Revert state
      if (activeRole === 'all') {
        setHiddenMenuIds(currentHiddenIds);
      } else {
        setRoleHiddenIds(roleHiddenIds);
      }
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2 text-foreground">Roles & Permissions</h2>
        <p className="text-sm text-muted-foreground">
          Manage roles and customize menu visibility for your workspace
        </p>
      </div>

      <div className="border rounded-lg bg-card border-border">
        <div className="px-6 py-4 flex items-center justify-between border-b border-border">
          <div className="flex items-center gap-3">
            <Menu className="w-5 h-5 text-muted-foreground" />
            <h3 className="text-lg font-semibold">Menu Customizer</h3>
          </div>
          <div>
            <Select value={activeRole} onValueChange={setActiveRole}>
              <SelectTrigger className="w-48 bg-background">
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="member">Members</SelectItem>
                <SelectItem value="manager">Managers</SelectItem>
                <SelectItem value="admin">Admins</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="p-6">
          <div className="text-muted-foreground mb-6">
            <p>Customize your sidebar navigation structure and items.</p>
            <p className="text-xs mt-1 text-muted-foreground/70">
              Toggle items to show or hide them for <strong>{activeRole === 'all' ? 'All Roles' : `${activeRole.charAt(0).toUpperCase()}${activeRole.slice(1)}s`}</strong>. Changes reflect immediately.
            </p>
          </div>

          <div className="space-y-4">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.id} className="rounded-lg border overflow-hidden border-border bg-muted/50">
                  <div className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-3">
                      <Icon className="w-5 h-5 text-muted-foreground" />
                      <span className="font-medium text-foreground">
                        {item.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-4">
                      {item.hasSubItems && (
                        <button
                          onClick={() => toggleMenuCollapse(item.id)}
                          className="p-1 rounded hover:bg-accent transition-colors text-muted-foreground"
                        >
                          {collapsedMenus[item.id] ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      )}
                      <Switch
                        checked={!currentHiddenIds.includes(item.id)}
                        onCheckedChange={(checked) => handleMenuToggle(item.id, checked)}
                      />
                    </div>
                  </div>

                  {item.hasSubItems && item.subItems && !collapsedMenus[item.id] && (
                    <div className="px-4 pb-4 pl-12 space-y-3 border-t border-border pt-4">
                      {item.subItems.map((subItem) => {
                        const SubIcon = subItem.icon;
                        return (
                          <div key={subItem.id} className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <SubIcon className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm text-foreground">
                                {subItem.label}
                              </span>
                            </div>
                            <Switch
                              checked={!currentHiddenIds.includes(subItem.id)}
                              onCheckedChange={(checked) => handleMenuToggle(subItem.id, checked)}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
